"use client";

import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  CreditCard,
  Gift,
  Loader2,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { useSession } from "@/app/hooks/useSession";
import { SettingsSection } from "@/components/settings/SettingsSection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { trackSubscription } from "@/lib/analytics";
import { fadeVariants } from "@/lib/animations";
import { getDisplayPriceInterval, getYearlySavingsPercent } from "@/lib/pricing";

interface SubscriptionData {
  isPremium: boolean;
  isAppleSubscription?: boolean;
  subscription: {
    id: string;
    status: string;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
    cancelAt: number | null;
    interval: "month" | "year";
    amount: number;
    currency: string;
    tier: string;
  } | null;
}

interface CreditData {
  hasCredits: boolean;
  balance: number;
  currency: string;
}

interface SubscriptionSectionProps {
  onCancelClick: () => void;
  subscriptionRefreshKey: number;
  onUpgradeClick: () => void;
}

export function SubscriptionSection({
  onCancelClick,
  subscriptionRefreshKey,
  onUpgradeClick,
}: SubscriptionSectionProps) {
  const t = useTranslations("premium");
  const locale = useLocale();
  const { update } = useSession();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [creditData, setCreditData] = useState<CreditData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("/api/subscription/status");
      setSubscriptionData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || t("failedToLoadSubscription"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchCredits = useCallback(async () => {
    try {
      const response = await axios.get("/api/subscription/credits");
      setCreditData(response.data);
    } catch (err: any) {
      console.error("Failed to fetch credits:", err);
      // Don't set error state - credits are optional info
    }
  }, []);

  useEffect(() => {
    // Make sure to fetch subscription status when the refresh key changes
    // Triggered from parent components on cancelling subscription
    // in dev mode this gets triggered twice, but it's fine
    fetchSubscriptionStatus();
    fetchCredits();
  }, [subscriptionRefreshKey, fetchSubscriptionStatus, fetchCredits]);

  const handleChangeInterval = async (newInterval: "monthly" | "yearly") => {
    setActionLoading(true);
    setError(null);
    const previousInterval =
      subscriptionData?.subscription?.interval === "month" ? "monthly" : "yearly";
    try {
      await axios.post("/api/subscription/change", { interval: newInterval });
      await fetchSubscriptionStatus();
      // Refresh session to update premium tier in UI
      update();

      // Track subscription change
      trackSubscription("changed", {
        interval: newInterval,
        previousInterval,
      });

      const description = newInterval === "monthly" ? t("monthly") : t("yearly");
      toast.success(t("billingChangedSuccess", { interval: description }), {
        description: t("billingChangedDescription", { description }),
      });
    } catch (err: any) {
      setError(err.response?.data?.error || t("failedToChangeSubscription"));
      toast.error(t("failedToChangePlan"), {
        description: err.response?.data?.error || t("pleaseTryAgainLater"),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const response = await axios.post("/api/subscription/cancel", {
        reactivate: true,
      });
      await fetchSubscriptionStatus();
      // Refresh session to update premium status
      update();
      toast.success(t("subscriptionReactivated"), {
        description: response.data.messageKey
          ? t(response.data.messageKey as any)
          : t("subscriptionContinueNormal"),
      });
    } catch (err: any) {
      setError(err.response?.data?.error || t("failedToReactivate"));
      toast.error(t("failedToReactivate"), {
        description: err.response?.data?.error || t("pleaseTryAgainLater"),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const response = await axios.post("/api/subscription/portal");
      window.location.href = response.data.url;
    } catch (err: any) {
      setError(err.response?.data?.error || t("failedToOpenBillingPortal"));
      setActionLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const subscription = subscriptionData?.subscription;
  const isMonthly = subscription?.interval === "month";
  const isYearly = subscription?.interval === "year";
  const isCanceling = subscription?.cancelAtPeriodEnd;

  // Format credit amount for display
  const formatCreditAmount = (balance: number, currency: string) => {
    const amount = balance / 100; // Convert cents to dollars
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  return (
    <SettingsSection className="space-y-6">
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            className="flex items-center justify-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Loader2 className="w-8 h-8 animate-spin" />
          </motion.div>
        ) : !subscriptionData?.isPremium ? (
          <motion.div
            key="no-subscription"
            className="space-y-4"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Show credit banner if user has credits but no subscription */}
            {creditData?.hasCredits ? (
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <Gift className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                      {t("youHaveCredits")}
                    </h3>
                    <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                      {t("creditsAvailableAmount", {
                        amount: formatCreditAmount(creditData.balance, creditData.currency),
                      })}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                      {t("subscribeToUseCredits")}
                    </p>
                    {onUpgradeClick && (
                      <button
                        type="button"
                        onClick={onUpgradeClick}
                        className="cursor-pointer text-sm font-medium text-green-900 dark:text-green-100 underline hover:no-underline mt-1 inline-block"
                      >
                        {t("upgradeToPremium")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t("noActiveSubscription")}</p>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="subscription-content"
            className="space-y-6"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
          >
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Apple Subscription Banner */}
            {subscriptionData?.isAppleSubscription && (
              <div className="space-y-4">
                <div>
                  {/* <h3 className="font-semibold mb-2">{t("appleSubscriptionTitle")}</h3> */}
                  <p className="text-sm text-muted-foreground">
                    {t("appleSubscriptionDescription")}
                  </p>
                </div>
                {subscription?.currentPeriodEnd && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t("renewalDate")}</span>
                    <span className="font-medium">{formatDate(subscription.currentPeriodEnd)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Current Plan - Only show for Stripe subscriptions */}
            {!subscriptionData?.isAppleSubscription && (
              <div className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      {isMonthly ? t("premiumMonthly") : t("premiumYearly")}
                      <Badge variant="secondary" className="font-normal">
                        {subscription?.status}
                      </Badge>
                    </h3>
                    <p className="text-2xl font-bold text-primary mt-1">
                      {getDisplayPriceInterval(isMonthly ? "monthly" : "yearly", t, locale)}
                    </p>
                  </div>
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>

                {isCanceling && subscription?.cancelAt && (
                  <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-950/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                          {t("subscriptionCanceling")}
                        </p>
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          {t("premiumAccessEndsOn")}{" "}
                          <span className="font-semibold">{formatDate(subscription.cancelAt)}</span>
                        </p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                          {t("reactivateAnytime")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Billing Info - Only show for Stripe subscriptions */}
            {!subscriptionData?.isAppleSubscription && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {isCanceling ? t("subscriptionEnds") : t("nextBillingDate")}
                  </span>
                  <span className="font-medium">
                    {isCanceling
                      ? subscription?.cancelAt
                        ? formatDate(subscription.cancelAt)
                        : "Unknown"
                      : subscription?.currentPeriodEnd
                        ? formatDate(subscription.currentPeriodEnd)
                        : "N/A"}
                  </span>
                </div>

                {/* Credit Display */}
                {creditData?.hasCredits && (
                  <div className="flex items-center gap-2 text-sm">
                    <Gift className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-muted-foreground">{t("referralCredits")}</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {formatCreditAmount(creditData.balance, creditData.currency)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Change Plan Options - Only show for Stripe subscriptions */}
            {!subscriptionData?.isAppleSubscription && !isCanceling && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-muted-foreground">{t("changePlan")}</h4>
                <div className="grid gap-3">
                  {isMonthly && (
                    <button
                      onClick={() => handleChangeInterval("yearly")}
                      disabled={actionLoading}
                      className="cursor-pointer p-4 text-left border-2 border-border hover:border-primary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                            <span className="font-semibold">{t("switchToYearly")}</span>
                            <Badge variant="secondary" className="text-xs">
                              {t("savePercent", {
                                percent: getYearlySavingsPercent(),
                              })}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {getDisplayPriceInterval("yearly", t, locale)}
                          </p>
                        </div>
                      </div>
                    </button>
                  )}

                  {isYearly && (
                    <button
                      onClick={() => handleChangeInterval("monthly")}
                      disabled={actionLoading}
                      className="cursor-pointer p-4 text-left border-2 border-border hover:border-primary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <TrendingDown className="w-4 h-4 text-muted-foreground" />
                            <span className="font-semibold">{t("switchToMonthly")}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {getDisplayPriceInterval("monthly", t, locale)}
                          </p>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Actions - Only show for Stripe subscriptions */}
            {!subscriptionData?.isAppleSubscription && (
              <div className="space-y-2 pt-2 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleManageBilling}
                  disabled={actionLoading}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("loading")}
                    </>
                  ) : (
                    t("manageBillingInvoices")
                  )}
                </Button>

                {!isCanceling ? (
                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground hover:text-destructive"
                    onClick={onCancelClick}
                    disabled={actionLoading}
                  >
                    {t("cancelSubscription")}
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleReactivateSubscription}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t("reactivating")}
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        {t("reactivateSubscription")}
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </SettingsSection>
  );
}
